import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Save, Trash2, Plus, Mail, FileText, Layers, Download, FileSpreadsheet, FileType2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { KLogo } from "@/components/KLogo";

const FIELD_LABELS = {
  nav: "Navigation", hero: "Hero", marquee: "Marquee", about: "À propos",
  services: "Services (intro)", africa: "Bande Guinée/Afrique", contact: "Contact", footer: "Pied de page",
};

// ---------- deep helpers ----------
const clone = (o) => JSON.parse(JSON.stringify(o));
const setByPath = (obj, path, value) => {
  const next = clone(obj);
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
  return next;
};

const Field = ({ label, value, onChange }) => {
  const long = typeof value === "string" && value.length > 60;
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-gris mb-1">{label}</span>
      {long ? (
        <textarea
          value={value} rows={3} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-ligne bg-white px-3 py-2 text-sm focus:border-rouge outline-none"
        />
      ) : (
        <input
          value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-ligne bg-white px-3 py-2 text-sm focus:border-rouge outline-none"
        />
      )}
    </label>
  );
};

// ---------- Login ----------
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("khuma_token", data.token);
      onLogin(data.user);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-noir px-4 grain">
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-2xl bg-creme p-8 shadow-xl">
        <KLogo className="h-9 w-auto mb-6" />
        <h1 className="font-serif text-2xl text-noir">Administration</h1>
        <p className="text-sm text-gris mb-6">Connectez-vous pour gérer le contenu.</p>
        {err && <div className="mb-4 rounded-md bg-rouge/10 text-rouge text-sm px-3 py-2" data-testid="admin-login-error">{err}</div>}
        <label className="block mb-4">
          <span className="block text-xs uppercase tracking-wider text-gris mb-1">Email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email"
            className="w-full rounded-md border border-ligne bg-white px-3 py-2.5 text-sm focus:border-rouge outline-none" />
        </label>
        <label className="block mb-6">
          <span className="block text-xs uppercase tracking-wider text-gris mb-1">Mot de passe</span>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password"
            className="w-full rounded-md border border-ligne bg-white px-3 py-2.5 text-sm focus:border-rouge outline-none" />
        </label>
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-rouge px-5 py-3 text-sm text-white hover:bg-[#c5291a] transition-colors disabled:opacity-60">
          {loading && <Loader2 size={16} className="animate-spin" />} Se connecter
        </button>
      </form>
    </div>
  );
};

// ---------- Content editor ----------
const ContentEditor = () => {
  const [draft, setDraft] = useState(null);
  const [langTab, setLangTab] = useState("fr");
  const [section, setSection] = useState("hero");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then(({ data }) => setDraft(data)); }, []);

  const onChange = (path, value) => setDraft((d) => setByPath(d, path, value));

  const renderNode = (value, path, labelKey) => {
    if (typeof value === "string") {
      return <Field key={path.join(".")} label={labelKey} value={value} onChange={(v) => onChange(path, v)} />;
    }
    if (Array.isArray(value)) {
      return (
        <div key={path.join(".")} className="space-y-3">
          <span className="block text-xs font-semibold text-noir">{labelKey}</span>
          {value.map((item, i) => (
            <div key={path.concat(i).join(".")} className="rounded-lg border border-ligne bg-creme/40 p-3 space-y-3">
              {renderNode(item, path.concat(i), `#${i + 1}`)}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div key={path.join(".")} className="space-y-3">
        {Object.entries(value).map(([k, v]) => renderNode(v, path.concat(k), k))}
      </div>
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { fr: draft.fr, en: draft.en });
      toast.success("Contenu enregistré");
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  if (!draft) return <div className="p-8 text-gris">Chargement…</div>;
  const sections = Object.keys(draft[langTab]);

  return (
    <div data-testid="admin-content-form">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="inline-flex rounded-full border border-ligne overflow-hidden">
          {["fr", "en"].map((l) => (
            <button key={l} onClick={() => setLangTab(l)} data-testid={`content-lang-${l}`}
              className={`px-5 py-2 text-sm ${langTab === l ? "bg-rouge text-white" : "bg-white text-noir"}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="content-save-btn"
          className="inline-flex items-center gap-2 rounded-full bg-noir px-5 py-2.5 text-sm text-white hover:bg-noir/90 disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {sections.map((s) => (
          <button key={s} onClick={() => setSection(s)}
            className={`px-3 py-1.5 rounded-full text-xs ${section === s ? "bg-or text-white" : "bg-white border border-ligne text-gris"}`}>
            {FIELD_LABELS[s] || s}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-ligne bg-white p-5 max-w-2xl">
        {renderNode(draft[langTab][section], [langTab, section], FIELD_LABELS[section] || section)}
      </div>
    </div>
  );
};

// ---------- Services editor ----------
const ServicesEditor = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/services").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  const update = (id, body) => api.put(`/admin/services/${id}`, body).then(() => { toast.success("Service mis à jour"); load(); });
  const remove = (id) => api.delete(`/admin/services/${id}`).then(() => { toast.success("Supprimé"); load(); });
  const add = () => api.post("/admin/services", { order: items.length + 1, fr: { title: "Nouveau service", desc: "" }, en: { title: "New service", desc: "" } }).then(() => { load(); });

  return (
    <div data-testid="admin-services">
      <div className="flex justify-end mb-4">
        <button onClick={add} className="inline-flex items-center gap-2 rounded-full bg-noir px-4 py-2 text-sm text-white"><Plus size={16} /> Ajouter</button>
      </div>
      <div className="space-y-4 max-w-3xl">
        {items.map((svc) => (
          <ServiceRow key={svc.id} svc={svc} onSave={update} onDelete={remove} />
        ))}
      </div>
    </div>
  );
};

const ServiceRow = ({ svc, onSave, onDelete }) => {
  const [s, setS] = useState(svc);
  return (
    <div className="rounded-xl border border-ligne bg-white p-4">
      <div className="flex items-center gap-3 mb-3">
        <input type="number" value={s.order} onChange={(e) => setS({ ...s, order: Number(e.target.value) })}
          className="w-16 rounded-md border border-ligne px-2 py-1 text-sm" />
        <span className="text-xs text-gris">ordre</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => onSave(s.id, { order: s.order, fr: s.fr, en: s.en })} className="inline-flex items-center gap-1 rounded-md bg-noir px-3 py-1.5 text-xs text-white"><Save size={14} /> Enregistrer</button>
          <button onClick={() => onDelete(s.id)} className="inline-flex items-center gap-1 rounded-md bg-rouge/10 text-rouge px-3 py-1.5 text-xs"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {["fr", "en"].map((l) => (
          <div key={l} className="space-y-2">
            <span className="text-xs font-semibold text-or">{l.toUpperCase()}</span>
            <input value={s[l].title} onChange={(e) => setS({ ...s, [l]: { ...s[l], title: e.target.value } })}
              className="w-full rounded-md border border-ligne px-3 py-2 text-sm" placeholder="Titre" />
            <textarea value={s[l].desc} rows={2} onChange={(e) => setS({ ...s, [l]: { ...s[l], desc: e.target.value } })}
              className="w-full rounded-md border border-ligne px-3 py-2 text-sm" placeholder="Description" />
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Submissions ----------
const Submissions = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/submissions").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  const remove = (id) => api.delete(`/admin/submissions/${id}`).then(() => { load(); });
  const markRead = (id) => api.put(`/admin/submissions/${id}/read`).then(() => { load(); });

  if (items.length === 0) return <div className="text-gris p-8 text-center" data-testid="admin-submissions">Aucun message reçu.</div>;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-submissions">
      {items.map((m) => (
        <div key={m.id} className={`rounded-xl border p-4 ${m.read ? "border-ligne bg-white" : "border-or bg-creme/40"}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-noir">{m.name} {!m.read && <span className="ml-2 text-[10px] uppercase bg-rouge text-white px-2 py-0.5 rounded-full">nouveau</span>}</div>
              <a href={`mailto:${m.email}`} className="text-sm text-rouge">{m.email}</a>
              {m.subject && <div className="text-sm text-gris mt-1">Sujet : {m.subject}</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              {!m.read && <button onClick={() => markRead(m.id)} className="text-xs rounded-md bg-noir px-2 py-1 text-white">Lu</button>}
              <button onClick={() => remove(m.id)} className="text-rouge"><Trash2 size={16} /></button>
            </div>
          </div>
          <p className="mt-3 text-sm text-noir/80 whitespace-pre-wrap">{m.message}</p>
          <div className="mt-2 text-[11px] text-gris">{new Date(m.created_at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
};

// ---------- Documents ----------
const EXT_META = {
  pdf: { label: "PDF", Icon: FileText },
  docx: { label: "Word", Icon: FileType2 },
  xlsx: { label: "Excel", Icon: FileSpreadsheet },
};
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

const Documents = () => {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState("");
  const [regen, setRegen] = useState(false);

  const load = useCallback(() => {
    return api.get("/admin/documents").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setRegen(true);
    try {
      await api.post("/admin/documents/regenerate");
      await load();
      toast.success("Documents régénérés");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la régénération");
    } finally { setRegen(false); }
  };

  const download = async (name) => {
    setBusy(name);
    try {
      const { data } = await api.get(`/admin/documents/${name}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
    finally { setBusy(""); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const doc of groups || []) {
      if (!map.has(doc.category)) map.set(doc.category, []);
      map.get(doc.category).push(doc);
    }
    return Array.from(map, ([category, docs]) => ({ category, docs }));
  }, [groups]);

  if (!groups) return <div className="p-8 text-gris">Chargement…</div>;

  return (
    <div className="max-w-3xl" data-testid="admin-documents">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-gris">Régénérez après une modification du barème pour actualiser tous les modèles.</p>
        <button onClick={regenerate} disabled={regen} data-testid="regenerate-docs-btn"
          className="inline-flex items-center gap-2 rounded-full bg-noir px-4 py-2 text-sm text-white hover:bg-noir/90 disabled:opacity-60">
          {regen ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Régénérer les documents
        </button>
      </div>
      {groups.length === 0 ? (
        <div className="p-8 text-gris text-center">Aucun document disponible.</div>
      ) : (
        <div className="space-y-8">
          {grouped.map(({ category, docs }) => (
            <div key={category}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-rouge mb-3">{category}</h3>
              <div className="space-y-3">
                {docs.map((doc) => (
                  <div key={doc.key} className="rounded-xl border border-ligne bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`document-${doc.key}`}>
                    <div className="font-medium text-noir">{doc.label}</div>
                    <div className="flex flex-wrap gap-2">
                      {doc.files.map((f) => {
                        const meta = EXT_META[f.ext] || { label: f.ext.toUpperCase(), Icon: FileText };
                        const Icon = meta.Icon;
                        return (
                          <button key={f.name} onClick={() => download(f.name)} disabled={busy === f.name}
                            data-testid={`download-${f.name}`}
                            className="inline-flex items-center gap-2 rounded-full border border-ligne bg-creme/60 px-3.5 py-1.5 text-xs text-noir hover:bg-creme hover:border-or transition-colors disabled:opacity-60">
                            {busy === f.name ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} className="text-or" />}
                            {meta.label} <span className="text-gris">· {fmtSize(f.size)}</span>
                            <Download size={13} className="text-gris" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Admin shell ----------
const TABS = [
  { key: "content", label: "Contenu", icon: FileText },
  { key: "services", label: "Services", icon: Layers },
  { key: "submissions", label: "Messages", icon: Mail },
  { key: "documents", label: "Documents", icon: Download },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("content");

  useEffect(() => {
    const token = localStorage.getItem("khuma_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => localStorage.removeItem("khuma_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem("khuma_token"); setUser(null); };

  if (checking) return <div className="min-h-screen grid place-items-center bg-creme"><Loader2 className="animate-spin text-rouge" /></div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-creme" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-creme/90 backdrop-blur border-b border-ligne">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <KLogo className="h-8 w-auto" />
            <span className="text-sm text-gris hidden sm:inline">Administration</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-gris hover:text-noir">Voir le site</a>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm text-noir hover:text-rouge"><LogOut size={16} /> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            return (
              <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`admin-tab-${tb.key}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ${tab === tb.key ? "bg-rouge text-white" : "bg-white border border-ligne text-gris"}`}>
                <Icon size={16} /> {tb.label}
              </button>
            );
          })}
        </div>

        {tab === "content" && <ContentEditor />}
        {tab === "services" && <ServicesEditor />}
        {tab === "submissions" && <Submissions />}
        {tab === "documents" && <Documents />}
      </div>
    </div>
  );
}
